import React, { useEffect, useState, useTransition } from "react";
import "./UserProfile.scss";
import { Button, DatePicker, Input, Select } from "components/form";
import ChangeUserPassword from "./ChangeUserPassword";
import { useDispatch, useSelector } from "react-redux";
import { removeErrorFields, validateInput } from "validation";
import { extractUserDetail, multiLangStaticObject } from "constant/staticData";
import { userProfile } from "validation/schema/userProfile";
import { masterCityList } from "services/master";
import { getFromLocal } from "config/axiosInstance";
import { updateUserPicApi, updateUserProfile } from "services/userProfile";
import { toastMessage } from "utils/toastMessageHelper";
import { setUserInfo } from "store/slices/userInfoSlice";
import useButtonLoader from "hooks/useButtonLoader";
import { adjustDateByYears, toDate } from "utils/dateHelper";
import { removeEmptyValues } from "utils/commonHelper";
import { showImageWithGender } from "utils/mediaHelper";
import CoverImageCropper from "components/modals/CoverImageCropper";
import { constant } from "constant/constantValue";
import { useTranslation } from "react-i18next";

const UserProfile = () => {
  const { userInfo } = useSelector((state) => state.userInfo);
  const regionList = useSelector((state) => state.regionList);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  // Set all value
  const [inputData, setInputData] = useState({});
  const [region, setRegion] = useState([]);
  const [city, setCity] = useState([]);
  const [error, setError] = useState({});
  const [imageModal, setImageModal] = useState(false);

  // Hook
  const [profileButtonLoader, setProfileButtonLoader] = useButtonLoader(
    t("EDIT_PROFILE.saveChange")
  );
  const [cropPicButtonLoader, setCropButtonLoader] = useButtonLoader(
    t("EDIT_PROFILE.crop")
  );

  // function handler

  // Fetch City List on the basis of state id handler function
  const fetchCityList = async (stateId, language) => {
    try {
      const reqData = { stateId, language };
      const responseData = await masterCityList(reqData);
      const {
        data: { city = {} },
        status,
      } = responseData;
      if (status === 200) {
        setCity(city);
      }
    } catch (e) {
      console.log("Error:", e.message);
    }
  };

  // Input change handler
  const changeHandler = (event) => {
    const { name, value } = event.target;
    // setInputData({ ...inputData, [name]: value });
    setInputData((prevInputData) => {
      if (name === "region" && prevInputData[name] !== value) {
        return {
          ...prevInputData,
          [name]: value,
          city: "",
          neighbourhoodDistrict: "",
        };
      }
      return {
        ...prevInputData,
        [name]: value,
      };
    });
    setError((prevError) => removeErrorFields(prevError, name));
  };

  // input validation
  const validation = () => {
    const { errors, status } = validateInput(
      inputData,
      "updateProfieSchema",
      userProfile,
      null
    );
    setError(errors);
    return status;
  };

  // Update Profile Handler
  const updateUserProfileHandler = async (file) => {
    if (validation()) {
      try {
        setProfileButtonLoader(true);
        const {
          businessEmail,
          personalEmail,
          mobile,
          profileUrl,
          ...userData
        } = inputData;
        const reqData = {
          ...userData,
          token: getFromLocal() || "",
        };

        const responseData = await updateUserProfile(reqData);
        const { message = "", status: requstStatus } = responseData;
        if (requstStatus === 200) {
          toastMessage(message, "success", "updateUserProfile");
          const selectedUserDetail = extractUserDetail(
            removeEmptyValues(inputData)
          );
          dispatch(setUserInfo(selectedUserDetail));
        }
      } catch (e) {
        console.log("Error:", e.message);
      } finally {
        setProfileButtonLoader(false);
      }
    }
  };

  // updateProfilePic
  const updateUserPicHandler = async (imageFile) => {
    try {
      setCropButtonLoader(true);
      const reqData = {
        token: getFromLocal(),
      };
      const responseData = await updateUserPicApi(reqData, imageFile);
      const {
        data: { profileUrl = "" },
        message = "",
        status,
      } = responseData || {};
      if (status === 200) {
        dispatch(
          setUserInfo({
            ...userInfo,
            profileUrl: profileUrl,
          })
        );
        toastMessage(message, "success", "updateUserProfile");
        setImageModal(false);
      }
    } catch (e) {
      console.log("Error:", e.message);
    } finally {
      setCropButtonLoader(false);
    }
  };

  useEffect(() => {
    setInputData(userInfo);
  }, [userInfo]);

  useEffect(() => {
    setRegion(regionList);
  }, [regionList]);

  // city List
  useEffect(() => {
    if (inputData && inputData?.region) {
      fetchCityList(inputData?.region, i18n.language);
    }
  }, [inputData?.region, i18n.language]);

  return (
    <>
      <div className="user_profile">
        <h3 className="dashboard_title">{t("EDIT_PROFILE.editProfile")}</h3>
        <div className="user_profile_content">
          <div className="profile_image">
            <CoverImageCropper
              prevImage={inputData?.profileUrl}
              idName={"profileImage"}
              imageClassName={"cstm_cropper_rounded"}
              cropPicButtonLoader={cropPicButtonLoader}
              updateUserPicHandler={updateUserPicHandler}
              imageModal={imageModal}
              setImageModal={setImageModal}
            >
              <img
                src={showImageWithGender(
                  null,
                  inputData?.profileUrl,
                  inputData?.gender,
                  ""
                )}
                alt="user detail Profile"
              />
              <label className="button w-100" htmlFor="profileImage">
                {t("EDIT_PROFILE.changePhoto")}
              </label>
            </CoverImageCropper>
          </div>
          <div className="form_content">
            <div className="_card">
              <h3 className="sub_title">{t("EDIT_PROFILE.personalDetail")}</h3>
              <form>
                <div className="form_field_wrap">
                  <div className="form_field">
                    <Input
                      type="text"
                      placeholder={t("EDIT_PROFILE.enterFirstName")}
                      label={t("EDIT_PROFILE.firstName")}
                      name="firstName"
                      value={inputData?.firstName || ""}
                      onChange={changeHandler}
                      error={error?.firstName}
                    />
                  </div>
                  <div className="form_field">
                    <Input
                      type="text"
                      placeholder={t("EDIT_PROFILE.enterLastName")}
                      label={t("EDIT_PROFILE.lastName")}
                      name="lastName"
                      value={inputData?.lastName || ""}
                      onChange={changeHandler}
                      error={error?.lastName}
                    />
                  </div>
                </div>
                <div className="form_field_wrap">
                  <div className="form_field">
                    <Select
                      label={t("EDIT_PROFILE.region")}
                      // options={region}
                      placeholder={t("EDIT_PROFILE.selectRegion")}
                      name="region"
                      value={inputData?.region || ""}
                      onChange={changeHandler}
                    >
                      {region &&
                        region.length &&
                        region.map((data, index) => (
                          <option
                            key={data?._id || index}
                            value={data?._id || ""}
                          >
                            {data?.name || ""}
                          </option>
                        ))}
                    </Select>
                  </div>
                  <div className="form_field">
                    <Input
                      type="email"
                      placeholder={t("EDIT_PROFILE.enterEmail")}
                      label={t("EDIT_PROFILE.email")}
                      value={inputData?.businessEmail || ""}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="form_field_wrap">
                  <div className="form_field">
                    <Input
                      type="number"
                      placeholder={t("EDIT_PROFILE.enterNumber")}
                      label={t("EDIT_PROFILE.mobileNumber")}
                      value={inputData?.mobile || ""}
                      disabled={true}
                      maxLength={constant.MOBILE_LENGHT}
                    />
                  </div>
                  <div className="form_field">
                    <Input
                      type="email"
                      placeholder={t("EDIT_PROFILE.enterPersonalEmail")}
                      label={t("EDIT_PROFILE.personalEmail")}
                      value={inputData?.personalEmail || ""}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="form_field_wrap">
                  <div className="form_field">
                    <Select
                      label={t("EDIT_PROFILE.gender")}
                      // options={USER_GENDER}
                      name="gender"
                      placeholder={t("EDIT_PROFILE.selectGender")}
                      value={inputData?.gender || ""}
                      onChange={changeHandler}
                    >
                      {multiLangStaticObject["MULTI_USER_GENDER"][
                        i18n.language || "en"
                      ] &&
                        multiLangStaticObject["MULTI_USER_GENDER"][
                          i18n.language || "en"
                        ].length &&
                        multiLangStaticObject["MULTI_USER_GENDER"][
                          i18n.language || "en"
                        ].map((data, index) => (
                          <option
                            key={data?.key ?? index}
                            value={data?.value || ""}
                          >
                            {data?.label || ""}
                          </option>
                        ))}
                    </Select>
                  </div>
                  <div className="form_field">
                    <DatePicker
                      label={t("EDIT_PROFILE.dob")}
                      name="dob"
                      value={inputData?.dob || ""}
                      onChange={(date) =>
                        setInputData({ ...inputData, dob: toDate(date) })
                      }
                      maxDate={adjustDateByYears(18)}
                      placeholder={t("EDIT_PROFILE.enterDob")}
                    />
                  </div>
                </div>

                <div className="form_field_wrap">
                  <div className="form_field">
                    <Select
                      label={t("EDIT_PROFILE.maritalStatus")}
                      placeholder={t("EDIT_PROFILE.selectMaritalStatus")}
                      name="martialStatus"
                      value={inputData?.martialStatus || ""}
                      onChange={changeHandler}
                    >
                      {multiLangStaticObject["MULTI_MARITAL_STATUSES"][
                        i18n.language || "en"
                      ] &&
                        multiLangStaticObject["MULTI_MARITAL_STATUSES"][
                          i18n.language || "en"
                        ].length &&
                        multiLangStaticObject["MULTI_MARITAL_STATUSES"][
                          i18n.language || "en"
                        ].map((data, index) => (
                          <option
                            key={data?.key ?? index}
                            value={data?.value || ""}
                          >
                            {data?.label || ""}
                          </option>
                        ))}
                    </Select>
                  </div>
                  <div className="form_field">
                    {/* <Input
                      type="text"
                      placeholder="Enter Education Level"
                      label="Education Level"
                      value={inputData?.username}
                      onChange={changeHandler}
                      error={error?.username}
                      extraError={error?.usernameErr}
                      maxLength={320}
                    /> */}
                    <Select
                      label={t("EDIT_PROFILE.educationLevel")}
                      placeholder={t("EDIT_PROFILE.selectEducationLevel")}
                      name="educationLevel"
                      value={inputData?.educationLevel || ""}
                      onChange={changeHandler}
                    >
                      {multiLangStaticObject["MULTI_EDUCATION_LEVEL"][
                        i18n.language || "en"
                      ] &&
                        multiLangStaticObject["MULTI_EDUCATION_LEVEL"][
                          i18n.language || "en"
                        ].length &&
                        multiLangStaticObject["MULTI_EDUCATION_LEVEL"][
                          i18n.language || "en"
                        ].map((data, index) => (
                          <option
                            key={data?.key ?? index}
                            value={data?.value || ""}
                          >
                            {data?.label || ""}
                          </option>
                        ))}
                    </Select>
                  </div>
                </div>

                <div className="form_field_wrap">
                  <div className="form_field">
                    <Input
                      type="text"
                      placeholder={t("EDIT_PROFILE.enterCompleteAddress")}
                      label={t("EDIT_PROFILE.completeAddress")}
                      name="address"
                      value={inputData?.address}
                      onChange={changeHandler}
                    />
                  </div>
                  <div className="form_field">
                    <Select
                      label={t("EDIT_PROFILE.annualIncome")}
                      placeholder={t("EDIT_PROFILE.selectAnnualIncome")}
                      name="annualIncome"
                      value={inputData?.annualIncome || ""}
                      onChange={changeHandler}
                    >
                      {multiLangStaticObject["MULTI_ANNUAL_INCOMES"][
                        i18n.language || "en"
                      ] &&
                        multiLangStaticObject["MULTI_ANNUAL_INCOMES"][
                          i18n.language || "en"
                        ].length &&
                        multiLangStaticObject["MULTI_ANNUAL_INCOMES"][
                          i18n.language || "en"
                        ].map((data, index) => (
                          <option
                            key={data?.key ?? index}
                            value={data?.value || ""}
                          >
                            {data?.label || ""}
                          </option>
                        ))}
                    </Select>
                  </div>
                </div>

                <div className="form_field_wrap">
                  <div className="form_field">
                    <Input
                      type="text"
                      placeholder={t(
                        "EDIT_PROFILE.enterEmployementInformation"
                      )}
                      label={t("EDIT_PROFILE.employementInformation")}
                      name="employementInfo"
                      value={inputData?.employementInfo}
                      onChange={changeHandler}
                    />
                  </div>
                  <div className="form_field">
                    <Select
                      label={t("EDIT_PROFILE.neighbourhood")}
                      name="neighbourhoodDistrict"
                      placeholder={t("EDIT_PROFILE.selectNeighbourhood")}
                      value={inputData?.neighbourhoodDistrict || ""}
                      onChange={changeHandler}
                      disabled={!inputData?.region}
                    >
                      {city &&
                        city.length &&
                        city.map((data, index) => (
                          <option
                            key={data?._id || index}
                            value={data?._id || ""}
                          >
                            {data?.name || ""}
                          </option>
                        ))}
                    </Select>
                  </div>
                </div>

                <div className="form_field_wrap">
                  <div className="form_field">
                    <Select
                      label={t("EDIT_PROFILE.city")}
                      options={city}
                      placeholder={t("EDIT_PROFILE.selectCity")}
                      name="city"
                      value={inputData?.city || ""}
                      onChange={changeHandler}
                      disabled={!inputData?.region}
                    >
                      {city &&
                        city.length &&
                        city.map((data, index) => (
                          <option
                            key={data?._id || index}
                            value={data?._id || ""}
                          >
                            {data?.name || ""}
                          </option>
                        ))}
                    </Select>
                  </div>
                  <div className="form_field">
                    <Select
                      label={t("EDIT_PROFILE.livingSituation")}
                      placeholder={t("EDIT_PROFILE.selectLivingSituation")}
                      name="livingSituation"
                      value={inputData?.livingSituation || ""}
                      onChange={changeHandler}
                    >
                      {" "}
                      {multiLangStaticObject["MULTI_LIVING_SITUATIONS"][
                        i18n.language || "en"
                      ] &&
                        multiLangStaticObject["MULTI_LIVING_SITUATIONS"][
                          i18n.language || "en"
                        ].length &&
                        multiLangStaticObject["MULTI_LIVING_SITUATIONS"][
                          i18n.language || "en"
                        ].map((data, index) => (
                          <option
                            key={data?.key ?? index}
                            value={data?.value || ""}
                          >
                            {data?.label || ""}
                          </option>
                        ))}
                    </Select>
                  </div>
                </div>

                <div className="button_wrap button_right">
                  <Button
                    type="button"
                    onClick={updateUserProfileHandler}
                    ref={profileButtonLoader}
                  >
                    {t("EDIT_PROFILE.saveChange")}
                  </Button>
                </div>
              </form>
            </div>
            {/* Change User Password */}
            <ChangeUserPassword />
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfile;
